import React from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

// Components
import ChatHistory from "./ChatHistory";
import Text from "new_components/atoms/Text";
import Icon from "new_components/atoms/Icon";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";

// Constants
import colors from "styles/colors";
import typography from "styles/typography";

const ChatHistoryModal = ({ 
  isOpen, 
  onClose, 
  chatRooms, 
  selectedRoomId, 
  onRoomSelect,
  onDeleteRoom 
}) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleChatSelect = (chatId) => {
    onRoomSelect(chatId);
    onClose(); // Close modal after selection
  };

  // Don't render anything if modal is closed
  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={handleOverlayClick}
    >
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          width: "min(90vw, 500px)",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${colors.neutral[200]}`,
          }}
        >
          <Text typographyStyle={typography.body.semibold}>
            Chat History
          </Text>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon 
              name={ICON_SET.clear} 
              size={20} 
              color={colors.neutral[500]}
            />
          </button>
        </div>
        
        {/* Body */}
        <div
          style={{
            padding: "16px 24px 24px",
            overflowY: "auto",
            flex: 1,
            minHeight: "200px",
            maxHeight: "60vh",
          }}
        >
          {(!chatRooms || chatRooms.length === 0) ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 20px",
                textAlign: "center",
              }}
            >
              <Icon 
                name={ICON_SET.bulletList} 
                size={48} 
                color={colors.neutral[300]}
                style={{ marginBottom: 16 }}
              />
              <Text 
                typographyStyle={typography.body.regular}
                color={colors.text.gray}
                style={{ marginBottom: 8 }}
              >
                No chat history yet
              </Text>
              <Text 
                typographyStyle={typography.smallText.regular}
                color={colors.text.gray}
              >
                Start a conversation to see your chat history here
              </Text>
            </div>
          ) : (
            <ChatHistory
              chatRooms={chatRooms}
              selectedRoomId={selectedRoomId}
              onRoomSelect={handleChatSelect}
              onDeleteRoom={onDeleteRoom}
            />
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document root
  return createPortal(modalContent, document.body);
};

ChatHistoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  chatRooms: PropTypes.array,
  selectedRoomId: PropTypes.number,
  onRoomSelect: PropTypes.func.isRequired,
  onDeleteRoom: PropTypes.func.isRequired,
};

export default ChatHistoryModal; 